// Spaces service — the Tickets-local ACL surface for the INTERNAL/PUBLIC
// partition (SKTICK-INTSPACE-001). Thin wrapper over the real /v1/spaces
// endpoints; all visibility is enforced server-side by RLS, so the client
// never decides who can see a ticket — only what it renders.
import { apiClient } from '../api-client';
import { SPACES } from '../../api-endpoints/api-endpoints';
import {
  ApiSpace, ApiSpaceMember, ApiMembershipEvent,
  CreateSpaceInput, GrantMemberInput, SpaceRole, ListEnvelope,
} from '../../schema/spaces/space.schema';

// A cheap, dependency-free unique id for idempotency keys. The server backs
// idempotency with a UNIQUE(org, idempotencyKey) on the event log, so a
// retried grant/revoke with the same key is a no-op replay.
function idemKey(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const spacesService = {
  /** Spaces the signed-in principal may enter (PUBLIC + granted INTERNAL). */
  listAccessible: (): Promise<ListEnvelope<ApiSpace>> =>
    apiClient.get<ListEnvelope<ApiSpace>>(SPACES.list),

  /** Every space in the org — admin management list (org-admin only). */
  listAll: (): Promise<ListEnvelope<ApiSpace>> =>
    apiClient.get<ListEnvelope<ApiSpace>>(SPACES.all),

  get: (id: string): Promise<ApiSpace> =>
    apiClient.get<ApiSpace>(SPACES.get(id)),

  create: (body: CreateSpaceInput): Promise<ApiSpace> =>
    apiClient.post<ApiSpace>(SPACES.create, body),

  /** Deactivate — never delete; history + tickets are retained read-only. */
  deactivate: (id: string): Promise<ApiSpace> =>
    apiClient.post<ApiSpace>(SPACES.deactivate(id)),

  listMembers: (id: string): Promise<ListEnvelope<ApiSpaceMember>> =>
    apiClient.get<ListEnvelope<ApiSpaceMember>>(SPACES.members(id)),

  listHistory: (id: string): Promise<ListEnvelope<ApiMembershipEvent>> =>
    apiClient.get<ListEnvelope<ApiMembershipEvent>>(SPACES.membershipHistory(id)),

  /** Grant (or change role) for a platform user — idempotent, safe to retry. */
  grant: (id: string, platformUserId: string, role: SpaceRole): Promise<ApiSpaceMember> =>
    apiClient.post<ApiSpaceMember>(SPACES.grant(id), {
      platformUserId, role, idempotencyKey: idemKey('grant'),
    } as GrantMemberInput),

  /** Revoke a member's access — immediate, append-logged, idempotent. */
  revoke: (id: string, platformUserId: string): Promise<{ revoked?: boolean } & Partial<ApiSpaceMember>> =>
    apiClient.delete(`${SPACES.revoke(id, platformUserId)}?idempotencyKey=${encodeURIComponent(idemKey('revoke'))}`),
};
